import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AppConfiguration } from "src/app/classes/configuration";
import { Coordinates } from "src/app/classes/geo-location-object";
import { MapSettings } from "src/app/classes/map-settings";
import { IMPERSONATION_ROLES } from "src/app/common/constants";
import { ConfigurationService } from "src/app/services/configuration.service";
import { CoordinatesService } from "src/app/services/coordinates.service";
import { LoggerService } from "src/app/services/logger.service";
import { MapSettingsService } from "src/app/services/map-settings.service";
import { SettingsService } from "src/app/services/settings.service";
import { ToastService } from "src/app/services/toast.service";
@Component({
  standalone: false,
  selector: "app-settings",
  templateUrl: "./settings.component.html",
  styleUrls: ["./settings.component.scss"],
})
export class SettingsComponent implements OnInit, OnDestroy {
  impersonateRole: string;
  roles: string[] =IMPERSONATION_ROLES;
  formGroup: FormGroup;
  unsubscribe$: Subject<boolean> = new Subject();
  configuration: AppConfiguration;
  public darkModeToggle = new FormControl(false);
  coordinatesSource = 'unknown';
  coordinates: Coordinates;
  lastCoordinateUpdate: string;
  panelOpenState = false;
  darkModeEnabled = false;

  constructor(
    private configurationService: ConfigurationService,
    private toastService: ToastService,
    private mapSettingsService: MapSettingsService,
    private logger: LoggerService,
    formBuilder: FormBuilder,
    private settingsService: SettingsService,
    private coordinatesService: CoordinatesService
  ) {
    this.initSettings(this.mapSettingsService.loadMapSettings());
    this.formGroup = formBuilder.group(
      this.mapSettingsService.loadMapSettings()
    );
    this.subscribeToDarkMode();
  }

  public saveMapSettings(mapSettings: MapSettings): void {
    this.mapSettingsService.saveMapSettings(mapSettings);
  }

  ngOnInit(): void {
    this.subscribeToConfiguration();
    /** Emitting DarkMode Toggle to AppComponent */
    this.darkModeToggle.valueChanges.subscribe((darkMode) => {
      this.settingsService.dispatchThemeChanged(darkMode);
      this.settingsService.updateDarkModeEnabled(darkMode);
      localStorage.setItem('prefAppTheme', darkMode + "");
    });
    let userPrefTheme = localStorage.getItem('prefAppTheme');
    // console.log("userPrefTheme :: ", userPrefTheme);
    if ( userPrefTheme == null || typeof userPrefTheme == "undefined") {
      userPrefTheme = "false"; //set default if first time
      localStorage.setItem('prefAppTheme', "false");
    } else {
      const userPrefThemeFlag = userPrefTheme === "true" ? true : false;
      this.settingsService.updateDarkModeEnabled(userPrefThemeFlag);
      this.darkModeToggle.setValue(userPrefThemeFlag, {
        onlySelf: true,
        emitEvent: false,
        emitModelToViewChange: false,
        emitViewToModelChange: false
      });
    }
    this.subscribeToCoordinatesSource();
    this.subscribeToCoordinates();

  }


  /* Move this logic to map component and subscribe to mapSettings  **/
  initSettings(mapSettings: MapSettings) {
    this.makeShortcutIcons(mapSettings);
    this.setLegend(mapSettings.displayLegendChecked);
  }

  setLegend(displayLegendChecked: boolean) {
    this.logger.debug("displayLegendChecked: " + displayLegendChecked, 'ui');
  }

  makeShortcutIcons(obj: object) {
    this.logger.debug("makeShortcutIcons", obj, 'ui');
  }

  subscribeToDarkMode(){
    this.settingsService.getDarkModeEnabled().subscribe((val: boolean) => {
      if(val !== null && val !== undefined){
        this.darkModeEnabled = val;
      }
    });
  }

  subscribeToConfiguration(): void {
    this.configurationService
      .getConfiguration()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((configuration) => {
        this.configuration = configuration;
      });
  }

  subscribeToCoordinatesSource(): void {
    this.coordinatesService
      .getCoordinatesSource()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((coordinatesSource) => {
        this.coordinatesSource = coordinatesSource;
      });
  }

  subscribeToCoordinates(){
    this.coordinatesService.getCoordinatesData().subscribe((data) => {
      if(data){
        this.lastCoordinateUpdate = new Date().toISOString();
        this.coordinates = data;
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

  public onFormSubmit(): void {
    const myMapSettings: MapSettings = this.formGroup.value;
    try {
      this.saveMapSettings(myMapSettings);
      this.toastService.show("Map Settings Saved!", {
        classname: "bg-success text-light",
        delay: 4000,
      });
    } catch (e) {
      this.toastService.show("Error Saving Map Settings!", {
        classname: "bg-danger text-light",
        delay: 4000,
      });
    }
  }

  setImpersonateRole(): void {
    if (this.impersonateRole) {
      this.settingsService.setImpersonateRole(this.impersonateRole);
      this.toastService.show(`Now impersonating role: ${this.impersonateRole}`, {
        classname: "bg-success text-light",
        delay: 4000,
      });
    }
  }

  clearLocalStorage(): void{
    localStorage.clear();
    location.reload();
  }

}
